import * as React from 'react'
import { Box, Button, FormHelperText } from '@material-ui/core'
import { Controlled as CodeMirror } from 'react-codemirror2'
import 'codemirror/lib/codemirror.css'
import 'codemirror/mode/javascript/javascript'

interface JsonEditorProps {
  value: string
  onChange: (value: string) => void
  height?: string
  readOnly?: boolean
  showFormatButton?: boolean
}

export const JsonEditor: React.FC<JsonEditorProps> = ({
  value,
  onChange,
  height = '240px',
  readOnly = false,
  showFormatButton = true
}) => {
  const [error, setError] = React.useState<string | null>(null)
  const editorRef = React.useRef<CodeMirror.Editor | null>(null)

  const validate = React.useCallback((nextValue: string) => {
    if (!nextValue || nextValue.trim() === '') {
      setError(null)
      return
    }

    try {
      JSON.parse(nextValue)
      setError(null)
    } catch (err) {
      setError((err as Error).message)
    }
  }, [])

  React.useEffect(() => {
    validate(value)
  }, [value, validate])

  const handleChange = React.useCallback(
    (nextValue: string) => {
      onChange(nextValue)
      validate(nextValue)
    },
    [onChange, validate]
  )

  React.useEffect(() => {
    if (editorRef.current) {
      editorRef.current.setSize('100%', height)
    }
  }, [height])

  const handleFormat = React.useCallback(() => {
    try {
      const parsed = JSON.parse(value || '{}')
      const formatted = JSON.stringify(parsed, null, 2)
      onChange(formatted)
      setError(null)
    } catch (err) {
      setError((err as Error).message)
    }
  }, [onChange, value])

  return (
    <Box width='100%'>
      {showFormatButton ? (
        <Box display='flex' justifyContent='flex-end' marginBottom={1} width='100%'>
          <Button size='small' variant='outlined' onClick={handleFormat} disabled={readOnly} aria-label='Format JSON'>
            Format
          </Button>
        </Box>
      ) : null}
      <Box width='100%' style={{ overflowX: 'auto' }}>
        <CodeMirror
          value={value}
          options={{
            mode: { name: 'javascript', json: true },
            lineNumbers: true,
            readOnly,
            lineWrapping: false
          }}
          editorDidMount={editor => {
            editorRef.current = editor
            editor.setSize('100%', height)
            const wrapper = editor.getWrapperElement()
            wrapper.style.border = '1px solid #e0e0e0'
            wrapper.style.background = '#fafafa'
            wrapper.style.borderRadius = '4px'
          }}
          onBeforeChange={(_editor, _data, nextValue) => {
            handleChange(nextValue)
          }}
        />
      </Box>
      {error ? <FormHelperText error>{error}</FormHelperText> : null}
    </Box>
  )
}
